import React, { useState, useMemo, useRef, useEffect } from "react";
import { X, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { buildCatalogIndex, searchCatalogIndexed } from "../utils/catalogSearch";

/**
 * HeroUI-style Popover wrapper component
 * Provides arrow, backdrop, and gradient styling
 */
function PopoverWrapper({ isOpen, onClose, triggerRef, children, placement = "right" }) {
  const [position, setPosition] = useState({ top: 0, left: 0, arrowTop: 0 });
  const popoverRef = useRef(null);

  // Calculate position based on trigger element
  useEffect(() => {
    if (isOpen && triggerRef?.current) {
      const updatePosition = () => {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const popoverWidth = 650;
        const popoverHeight = 470;
        const offset = 12; // Gap between trigger and popover

        let top, left, arrowTop;

        if (placement === "right") {
          // Calculate trigger's vertical center
          const triggerCenterY = triggerRect.top + triggerRect.height / 2;
          
          left = triggerRect.right + offset;
          
          // Check if popover would go off right edge
          if (left + popoverWidth > window.innerWidth - 20) {
            // Place on left instead
            left = triggerRect.left - popoverWidth - offset;
          }
          
          // Ideal position: center popup on trigger
          const idealTop = triggerCenterY - popoverHeight / 2;
          
          // Constrain to viewport
          const maxTop = window.innerHeight - popoverHeight - 20;
          const minTop = 20;
          top = Math.min(Math.max(idealTop, minTop), maxTop);
          
          // Calculate arrow position: where the trigger center is relative to the popup
          // Arrow should point to trigger's vertical center
          arrowTop = triggerCenterY - top;
          
          // Clamp arrow position to stay within popup bounds (with padding)
          const arrowMinTop = 24;
          const arrowMaxTop = popoverHeight - 24;
          arrowTop = Math.min(Math.max(arrowTop, arrowMinTop), arrowMaxTop);
        } else {
          // Default bottom placement
          top = triggerRect.bottom + offset;
          left = triggerRect.left;
          arrowTop = 0; // Not used for bottom placement
        }

        setPosition({ top, left, arrowTop });
      };

      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen, triggerRef, placement]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[9998]"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
            onClick={onClose}
          />
          
          {/* Popover Container */}
          <motion.div
            ref={popoverRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="fixed z-[9999]"
            style={{
              top: position.top,
              left: position.left,
              width: 650,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Arrow - dynamically positioned to point at trigger */}
            <div
              className="absolute w-3 h-3 rotate-45"
              style={{
                left: -6,
                top: position.arrowTop,
                marginTop: -6,
                background: "linear-gradient(135deg, #e5e5e5 0%, #e5e5e5 50%, transparent 50%, transparent 100%)",
                borderLeft: "1px solid #d4d4d4",
                borderBottom: "1px solid #d4d4d4",
              }}
            />
            
            {/* Popover Content */}
            <div
              className="rounded-xl overflow-hidden shadow-2xl"
              style={{
                border: "1px solid #d4d4d4",
                background: "linear-gradient(to bottom right, #ffffff, #f5f5f5)",
              }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

/**
 * Catalog Matches Popup Content
 * Contains the search input and results table
 */
function CatalogMatchesContent({
  lineItemName,
  catalog,
  onSelect,
  onClose,
  onSearchQueryChange,
  focusInput = true,
}) {
  const [searchQuery, setSearchQuery] = useState(lineItemName || "");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Sync searchQuery with lineItemName when it changes externally
  useEffect(() => {
    setSearchQuery(lineItemName || "");
  }, [lineItemName]);

  // Focus input on mount (optional - can be disabled for manual entry mode)
  useEffect(() => {
    if (focusInput) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [focusInput]);

  // Precompute catalog index ONCE when catalog changes (not on every keystroke)
  const catalogIndex = useMemo(() => {
    // Beginning of performance testing
    const startBuild = performance.now();
    // Line under is not part of the performance testing
    const index = buildCatalogIndex(catalog);

    const buildTime = performance.now() - startBuild;
    
    // DEBUG: Log data sizes and timing for performance analysis
    const rawCatalogSize = JSON.stringify(catalog).length;
    const indexSize = JSON.stringify(index).length;
    console.log('=== Catalog Data Size Analysis ===');
    console.log(`Raw catalog: ${catalog?.length || 0} items, ${(rawCatalogSize / 1024).toFixed(2)} KB`);
    console.log(`Indexed catalog: ${index?.length || 0} items, ${(indexSize / 1024).toFixed(2)} KB`);
    console.log(`Index overhead: ${((indexSize / rawCatalogSize - 1) * 100).toFixed(1)}% larger than raw`);
    console.log(`⏱️ Index build time: ${buildTime.toFixed(2)} ms`);
    console.log('Sample indexed item:', index?.[0]);
    console.log('==================================');
    // End of performance testing
    return index;
  }, [catalog]);

  // Search using precomputed index - only tokenizes/stems the query per keystroke
  const matches = useMemo(() => {
    const startSearch = performance.now();
    const results = searchCatalogIndexed(searchQuery, catalogIndex, Infinity); // No cap for perf testing
    const searchTime = performance.now() - startSearch;
    
    if (searchQuery.trim()) {
      console.log(`🔍 Search "${searchQuery}": ${results.length} results in ${searchTime.toFixed(2)} ms`);
    }
    
    return results;
  }, [searchQuery, catalogIndex]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < matches.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < matches.length) {
          onSelect(matches[selectedIndex]);
        } else if (matches.length > 0) {
          onSelect(matches[0]);
        }
        break;
      case "Escape":
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && listRef.current) {
      const selectedElement = listRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  return (
    <div style={{ maxHeight: "500px" }}>
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3"
        style={{ 
          borderBottom: "1px solid #e5e5e5",
          background: "linear-gradient(to bottom right, #fafafa, #f0f0f0)",
        }}
      >
        <h3 className="text-sm font-bold" style={{ color: "#1C1C1C" }}>
          Catalog Matches
        </h3>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200/50 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Search Input */}
      <div className="px-4 py-3" style={{ borderBottom: "1px solid #e5e5e5" }}>
        <div className="relative">
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2"
            style={{ color: "#727272" }}
          />
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchQuery(newValue);
              setSelectedIndex(-1);
              // Notify parent of search query change if callback provided
              if (onSearchQueryChange) {
                onSearchQueryChange(newValue);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search catalog..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ border: "1px solid #e5e5e5", color: "#424242" }}
          />
        </div>
      </div>

      {/* Results Table - fixed height for consistent popover size */}
      <div
        ref={listRef}
        className="overflow-y-auto"
        style={{ height: "350px" }}
      >
        {matches.length > 0 ? (
          <table className="w-full">
            <thead 
              className="sticky top-0"
              style={{ 
                background: "linear-gradient(to bottom right, #fafafa, #f0f0f0)",
                borderBottom: "1px solid #e5e5e5",
              }}
            >
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium" style={{ color: "#727272" }}>Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium" style={{ color: "#727272" }}>Code</th>
                <th className="px-3 py-2 text-left text-xs font-medium" style={{ color: "#727272" }}>QTY</th>
                <th className="px-3 py-2 text-left text-xs font-medium" style={{ color: "#727272" }}>Unit</th>
                <th className="px-3 py-2 text-left text-xs font-medium" style={{ color: "#727272" }}>Price</th>
                <th className="px-3 py-2 text-left text-xs font-medium" style={{ color: "#727272" }}>Category</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((item, index) => (
                <tr
                  key={item.id}
                  className={`cursor-pointer transition-colors ${
                    index === selectedIndex ? "bg-blue-50" : "hover:bg-gray-50/50"
                  }`}
                  onClick={() => onSelect(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <td className="px-3 py-2 text-sm truncate max-w-[180px]" style={{ color: "#1C1C1C" }}>{item.name}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: "#424242" }}>{item.code || "-"}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: "#424242" }}>{item.default_quantity || 1}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: "#424242" }}>{item.unit || "-"}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: "#424242" }}>{item.unit_price ? `$${item.unit_price}` : "-"}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: "#424242" }}>{item.category || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            {searchQuery.trim()
              ? "No matching items found"
              : "Start typing to search"}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Main CatalogMatchesPopup component
 * Combines the PopoverWrapper with CatalogMatchesContent
 */
export default function CatalogMatchesPopup({
  isOpen,
  triggerRef,
  lineItemName,
  catalog,
  onSelect,
  onClose,
  onSearchQueryChange,
  focusInput = true,
}) {
  return (
    <PopoverWrapper
      isOpen={isOpen}
      onClose={onClose}
      triggerRef={triggerRef}
      placement="right"
    >
      <CatalogMatchesContent
        lineItemName={lineItemName}
        catalog={catalog}
        onSelect={onSelect}
        onClose={onClose}
        onSearchQueryChange={onSearchQueryChange}
        focusInput={focusInput}
      />
    </PopoverWrapper>
  );
}
